/**
 * @file chart-default-config.util.ts
 * @description Computes default data query from customChartConfig without iframe
 * This enables early data fetching while iframe initializes in parallel
 *
 * Performance Impact: ~900ms savings by allowing data fetch to start 1+ second earlier
 */

import {
    getChartType,
    getColumns,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    getDefaultBatchSize,
    isTsHostedChart,
    newExperienceChartTypes,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { generateDefaultCustomChartConfigForTSHostedCharts } from '@thoughtspot/chart-lib/lib/core/custom-chart-config-utils';
import { envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import {
    convertCustomChartConfigToDataQueryInput,
    extractColumnIdsFromDimension,
} from '@thoughtspot/ts-chart-app';
import {
    AnswerColumn,
    AnswerColumnEffectiveType,
    ChartSpecificColumnType,
    ChartType,
    ChartViz,
    CustomChartAxisConfig,
    CustomChartConfig,
    DataQueryInput,
    SimpleAxis,
} from '/@services/generated/graphql-types';

/**
 * Per-chart-type MEASURE_NAMES/MEASURE_VALUES handling for the prefetch.
 *
 * The prefetch must send a query byte-identical to what the chart iframe
 * later sends, or Apollo's cache lookup misses and the same
 * GetAnswerDataByQuery fires twice. Cartesian/pivot include MN/MV; but the
 * iframe decides MN/MV per chart type, and on 26.8 (no shared
 * chart-query-builder) we mirror those decisions here from the config shape:
 *
 *  - Bubble/Scatter (point charts): the iframe strips MN/MV via
 *    `needsMnMvPostProcessing` — true when both axes are measures OR neither
 *    is (base-point-chart), and bubble additionally when a size column is
 *    present (bubble-chart). When it strips, we must NOT include MN/MV.
 *  - Donut (polar): the iframe never strips on shape but only ADDS MN/MV when
 *    a MEASURE_NAMES column sits in the category (x-axis) or trellis-by slot
 *    (base-polar-chart). Otherwise it emits none.
 *
 * These are the slot keys valkyrie's chart configs use (dimension.key).
 */
const SECTION_X_AXIS = 'x-axis';
const SECTION_Y_AXIS = 'y-axis';
const SECTION_SIZE = 'size';
const SECTION_TRELLIS_BY = 'trellis-by';

/** True if any surviving column in the given slot(s) is a measure. */
function sectionHasMeasure(
    customChartConfigs: CustomChartConfig[],
    columns: AnswerColumn[],
    sectionKeys: string[],
): boolean {
    return customChartConfigs.some(config =>
        (config.dimensions ?? []).some(
            dimension =>
                sectionKeys.includes(dimension.key) &&
                extractColumnIdsFromDimension(dimension).some(colId => {
                    const col = columns.find(c => c.id === colId);
                    return col?.type === AnswerColumnEffectiveType.Measure;
                }),
        ),
    );
}

/** True if the given slot(s) hold at least one surviving column. */
function sectionHasColumn(
    customChartConfigs: CustomChartConfig[],
    sectionKeys: string[],
): boolean {
    return customChartConfigs.some(config =>
        (config.dimensions ?? []).some(
            dimension =>
                sectionKeys.includes(dimension.key) &&
                extractColumnIdsFromDimension(dimension).length > 0,
        ),
    );
}

/** True if the MEASURE_NAMES column sits in the category or trellis-by slot. */
function configHasMeasureNamesInCategory(
    customChartConfigs: CustomChartConfig[],
    columns: AnswerColumn[],
): boolean {
    const mnCol = columns.find(
        c => c.chartSpecificColumnType === ChartSpecificColumnType.MeasureNames,
    );
    if (!mnCol) return false;
    return customChartConfigs.some(config =>
        (config.dimensions ?? []).some(
            dimension =>
                (dimension.key === SECTION_X_AXIS ||
                    dimension.key === SECTION_TRELLIS_BY) &&
                extractColumnIdsFromDimension(dimension).includes(mnCol.id),
        ),
    );
}

/**
 * Resolves whether the prefetch query for `chartType` should carry MN/MV, so
 * it byte-matches what the iframe emits for that type. `baseInclude` is the
 * cartesian default (client-side-MN/MV flag off and not MuzeStudio); the
 * per-type rules can only turn it OFF (point-chart strip) or gate it on the
 * config shape (donut), never force it on.
 */
function shouldIncludeMnMvForChartType(
    chartType: ChartType,
    baseInclude: boolean,
    customChartConfig: CustomChartConfig[],
    columns: AnswerColumn[],
): boolean {
    if (!baseInclude) return false;

    if (
        chartType === ChartType.AdvancedScatter ||
        chartType === ChartType.AdvancedBubble
    ) {
        const xHasMeasures = sectionHasMeasure(customChartConfig, columns, [
            SECTION_X_AXIS,
        ]);
        const yHasMeasures = sectionHasMeasure(customChartConfig, columns, [
            SECTION_Y_AXIS,
        ]);
        let needsStrip =
            (xHasMeasures && yHasMeasures) || (!xHasMeasures && !yHasMeasures);
        if (chartType === ChartType.AdvancedBubble) {
            needsStrip =
                needsStrip ||
                sectionHasColumn(customChartConfig, [SECTION_SIZE]);
        }
        return !needsStrip;
    }

    if (chartType === ChartType.AdvancedDonut) {
        return configHasMeasureNamesInCategory(customChartConfig, columns);
    }

    return baseInclude;
}

/**
 * Filters a simple axis (FlatAxis or MergedAxis) to only include valid columns
 * @param axis - The simple axis configuration (FlatAxis or MergedAxis)
 * @param availableColumnIds - Set of valid column IDs
 * @returns Filtered axis or null if no valid columns remain
 */
function filterSimpleAxis(
    axis: SimpleAxis,
    availableColumnIds: Set<string>,
): SimpleAxis | null {
    // Handle FlatAxis
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'FlatAxis') {
        return availableColumnIds.has(axis.column) ? axis : null;
    }

    // Handle MergedAxis
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'MergedAxis') {
        const filteredColumns = axis.columns.filter((colId: string) =>
            availableColumnIds.has(colId),
        );
        if (filteredColumns.length === 0) return null;
        return { ...axis, columns: filteredColumns };
    }

    return null;
}

/**
 * Filters a custom chart axis to only include columns that exist in availableColumnIds
 * @param axis - The axis configuration (FlatAxis, MergedAxis, or DualAxis)
 * @param availableColumnIds - Set of valid column IDs
 * @returns Filtered axis or null if no valid columns remain
 */
function filterAxis(
    axis: CustomChartAxisConfig,
    availableColumnIds: Set<string>,
): CustomChartAxisConfig | null {
    if (!axis) return null;

    // Handle DualAxis
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'DualAxis') {
        const filteredPrimary = filterSimpleAxis(
            axis.primary,
            availableColumnIds,
        );
        const filteredSecondary = filterSimpleAxis(
            axis.secondary,
            availableColumnIds,
        );

        // Only include if at least one side has columns
        if (!filteredPrimary && !filteredSecondary) return null;

        return {
            ...axis,
            primary: filteredPrimary || axis.primary,
            secondary: filteredSecondary || axis.secondary,
        };
    }

    // Handle FlatAxis and MergedAxis using filterSimpleAxis
    return filterSimpleAxis(axis, availableColumnIds);
}

/**
 * Filters customChartConfig to only include columns that exist in the available columns list
 * This handles both axis-driven and column-driven configurations
 * @param customChartConfigs - Array of custom chart configurations
 * @param availableColumns - Array of available columns
 * @returns Filtered customChartConfig array
 */
function filterCustomChartConfigColumns(
    customChartConfigs: CustomChartConfig[],
    availableColumns: AnswerColumn[],
): CustomChartConfig[] {
    if (!customChartConfigs || customChartConfigs.length === 0) {
        return customChartConfigs;
    }

    // Create a set of available column IDs for fast lookup
    const availableColumnIds = new Set(availableColumns.map(col => col.id));

    return customChartConfigs.map(config => {
        // Clone the config to avoid mutations
        const filteredConfig = { ...config };

        if (!filteredConfig.dimensions) {
            return filteredConfig;
        }

        // Filter each dimension
        filteredConfig.dimensions = filteredConfig.dimensions
            .map(dimension => {
                // Handle axis-driven configuration
                // eslint-disable-next-line no-underscore-dangle
                if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
                    const filteredAxes = (dimension.axes || [])
                        .map(axis => filterAxis(axis, availableColumnIds))
                        .filter(axis => axis !== null);

                    if (filteredAxes.length === 0) return null;

                    return { ...dimension, axes: filteredAxes };
                }

                // Handle column-driven configuration
                if (dimension.columns) {
                    const filteredColumns = dimension.columns.filter(colId =>
                        availableColumnIds.has(colId),
                    );

                    if (filteredColumns.length === 0) return null;

                    return { ...dimension, columns: filteredColumns };
                }

                return dimension;
            })
            .filter(dimension => dimension !== null);

        return filteredConfig;
    });
}

/**
 * Build initial data query for TS-hosted charts
 * This uses the proper dimension mapping that matches what the chart SDK does
 *
 * @param chartViz - Chart visualization metadata from GetChartWithData
 * @returns DataQueryInput to fetch data, or null if unable to compute
 */
export function buildInitialDataQueryForTSHostedCharts(
    chartViz: ChartViz,
): DataQueryInput | null {
    if (!chartViz) {
        return null;
    }

    const chartType = getChartType(chartViz);

    // Only compute for custom and TS-hosted charts
    if (!isTsHostedChart(chartType)) {
        return null;
    }

    const columns = getColumns(chartViz);

    if (columns.length === 0) {
        return null;
    }
    const maxDataPoints =
        chartViz.customVisualProps?.displayVisualProps?.display?.[
            'max-data-points'
        ];
    const defaultBatchSize =
        maxDataPoints === ''
            ? getDefaultBatchSize(chartType)
            : maxDataPoints ?? getDefaultBatchSize(chartType);

    const queryParams = {
        offset: 0,
        size: defaultBatchSize,
    };
    // For Muze phase-1 cartesian charts the BE owns default config generation.
    // Use the existing config if present; skip FE generation so we don't
    // override the BE-computed config with a stale FE-generated one.
    const isMuzeChart = newExperienceChartTypes.includes(chartType);
    let customChartConfig =
        chartViz.config.customChartConfig ||
        (isMuzeChart
            ? null
            : generateDefaultCustomChartConfigForTSHostedCharts(
                  chartViz,
                  chartType,
              ));

    // Filter out columns from customChartConfig that are not in the available columns list
    // This prevents issues when columns have been removed from the answer but still exist in the config
    if (customChartConfig) {
        customChartConfig = filterCustomChartConfigColumns(
            customChartConfig,
            columns,
        );
    }

    const isClientSideMnMvCalculationEnabled = isFeatureEnabled(
        FEATURE_FLAG_NAMES.ENABLE_CLIENT_SIDE_MN_MV_CALCULATION,
        {
            urlParamValue: envFlags.getValue(
                FEATURE_FLAG_NAMES.ENABLE_CLIENT_SIDE_MN_MV_CALCULATION,
            ),
        },
    );

    const shouldIncludeMnMvFields =
        !isClientSideMnMvCalculationEnabled &&
        chartType !== ChartType.MuzeStudio;

    // Mirror the iframe's per-chart-type MN/MV decision so the prefetch query
    // byte-matches (point charts strip; donut only adds when MN is in the
    // category/trellis slot). Cartesian/pivot are unchanged.
    const effectiveIncludeMnMvFields = customChartConfig
        ? shouldIncludeMnMvForChartType(
              chartType,
              shouldIncludeMnMvFields,
              customChartConfig,
              columns,
          )
        : shouldIncludeMnMvFields;

    const dataQueryInput: DataQueryInput = convertCustomChartConfigToDataQueryInput(
        customChartConfig,
        columns,
        queryParams,
        effectiveIncludeMnMvFields,
    );

    return dataQueryInput;
}

/**
 * Check if early data fetch is enabled and beneficial for this chart
 */
export function shouldPrefetchData(chartViz: ChartViz | null): boolean {
    if (!chartViz) {
        return false;
    }

    const chartType = getChartType(chartViz);

    // Only prefetch for custom/TS-hosted charts
    if (!isTsHostedChart(chartType) || chartType === ChartType.MuzeStudio) {
        return false;
    }

    // Must have columns - this is all we need now since we use ALL columns
    const columns = chartViz.columns || [];

    if (columns.length === 0) {
        return false;
    }

    return true;
}
